/*
Copied and adapted from https://github.com/Esri/react-arcgis/blob/master/src/ts/components/MapComposites.tsx

Copyright © 2017-2019 Esri

Licensed under the Apache License, Version 2.0 (the "License"); you may not use this file except in compliance
with the License. You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software distributed under the License is distributed on
an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied. See the License for the
specific language governing permissions and limitations under the License.

A copy of the license is available in the repository's LICENSE file.
TODO: Refactor
 */
import * as React from "react";
import { BaseProps } from "../../props";
import { eventMap } from "../../events";
import {StyledContainer} from "../styled-container/styled-container";
import EsriMap from "@arcgis/core/Map";
import EsriSceneView from "@arcgis/core/views/SceneView";

export interface SceneProps extends BaseProps {
    mapProperties?: __esri.MapProperties;
    viewProperties?: __esri.SceneViewProperties;
}

export class Scene extends React.Component<SceneProps, any> {
    constructor(props: SceneProps) {
        super(props);
        this.state = {
            mapContainerId: this.props.containerId,
            status: "loading"
        };
    }

    render(): React.ReactElement | null {
        const centerStyle = {
            left: "50%",
            marginRight: "-50%",
            position: "absolute",
            top: "50%",
            transform: "translate(-50%, -50%)"
        };

        const mapStyle = this.props.className ?
            this.props.style :
            {
                height: "100%",
                position: "relative",
                width: "100%",
                ...this.props.style
            };

        const loadElement = (
            this.props.loadElement ? this.props.loadElement :
                <h3 id="react-arcgis-loading-text">Wird geladen...</h3>
        );

        const failElement = (
            this.props.failElement ? this.props.failElement :
                <h3 id="react-arcgis-fail-text">Laden des ArcGIS API fehlgeschlagen.</h3>
        );

        if (this.state.status === "loaded") {
            if (!!this.props.childrenAsFunction) {
                return this.props.childrenAsFunction(this.state.map, this.state.view);
            }
            const childrenWithProps = React.Children.map(this.props.children, (child) => {
                const childEl = child as React.ReactElement<any>;
                return React.cloneElement(childEl, {
                    map: this.state.map,
                    view: this.state.view
                }
                );
            });
            return (
                <div id="base-container" style={mapStyle} className={this.props.className}>
                    <StyledContainer id={this.state.mapContainerId} style={{ width: "100%", height: "100%" }} />
                    {childrenWithProps}
                </div>
            );
        } else if (this.state.status === "loading") {
            return (
                <div id="base-container" style={mapStyle} className={this.props.className}>
                    <StyledContainer id={this.state.mapContainerId} style={{ width: "100%", height: "100%" }} />
                    <div style={centerStyle as any}>
                        {loadElement}
                    </div>
                </div>
            );
        }
        return (
            <div id="base-container" style={mapStyle} className={this.props.className}>
                <StyledContainer id={this.state.mapContainerId} style={{ width: "100%", height: "100%" }} />
                <div style={centerStyle as any}>
                    {failElement}
                </div>
            </div>
        );
    }

    loadMap() {
        const mapData = new Promise((resolve, reject) => {
            try {
                const map: __esri.Map = new EsriMap(this.props.mapProperties);  // Make the map
                const viewProperties: any = {
                    map,
                    container: this.props.containerId,
                    ...this.props.viewProperties
                };
                const view: __esri.View = new EsriSceneView(viewProperties);  // Make the view
                const typedView = view as __esri.SceneView;
                Object.keys(eventMap).forEach((key) => {  // Set view events to any user defined callbacks
                    if (this.props[key]) {
                        typedView.on(eventMap[key], this.props[key]);
                    }
                });
                view.when(() => {
                    resolve({ map, view });
                }, (err: Error) => {
                    reject(err);
                });
            } catch (err) {
                reject(err);
            }
        });
        return mapData;
    }

    componentDidMount() {
        // load the map after the component was mounted
        this.loadMap()
            .then(
                ({map, view}) => {
                    this.setState({
                        map,
                        view,
                        status: "loaded"
                    });
                    if (this.props.onLoad) {
                        this.props.onLoad(map, view);
                    }
                })
            .catch((e: Error) => {
                throw e;
            });
    }
}

